import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import PropTypes from "prop-types";
import { toast } from "react-toastify";
import { FaSpinner, FaPlus, FaTimes } from "react-icons/fa";
import { confirm } from "components/common";
import {
  getJournalWorkbench, saveJournalOverrides, createJournalFromWorkbench, pushJournalChanges,
  getPaymentRequest, startJournalAdjustment, cancelJournalAdjustment,
} from "redux/erp/paymentRequestSlice";
import JournalSection from "./JournalSection";
import JournalHeaderFields, { headerChanged, headerPayload, headerState } from "./JournalHeaderFields";
import ErpLogPanel from "./ErpLogPanel";
import { ActionButton, StateBadge } from "./journalUi";
import {
  AMOUNT_KEYS, DEFAULT_ACCOUNT_TYPES, DEFAULT_ITEM_SALES_TAX_GROUPS, FIELDS, editableFields, hsbcOffsetAccounts, lineId, newManualKey, renumber, toValues,
} from "./journalFields";
import HsbcOnlinePaymentNotice from "./HsbcOnlinePaymentNotice";
import HsbcPaymentFilePanel from "./HsbcPaymentFilePanel";
import PaymentDetailsFields from "./PaymentDetailsFields";
import JournalAdjustmentModal from "./JournalAdjustmentModal";
import { fetchVendorDetails, VENDOR_PAYMENT_FIELDS } from "./vendorDetailsApi";
import PaymentRequestSettingsModal from "../PaymentRequestSettingsModal";
import "./JournalWorkbench.css";

/**
 * Tab "Journal" — Kế toán / Kế toán trưởng điền bổ sung bút toán và đẩy
 * sang ERP qua API, thay vì mở AX.
 *
 * Mỗi journal của phiếu là một tab có header riêng (Journal name, currency, ngày, diễn
 * giải, nhà cung cấp) và dòng riêng. Journal chính là API (ghi nhận hoá đơn) hoặc APP
 * (chi tiền) — chọn ở Journal name; với API, Payment journal (APP) là tuỳ chọn, nhưng
 * lệnh chi ngân hàng cần có APP. Payment details dùng chung. Nút Create tạo mọi
 * journal còn thiếu.
 *
 * Mỗi journal chỉ tạo MỘT lần trên ERP — số journal ERP trả về là bản ghi, không xoá /
 * tạo lại. ERP chỉ ghi dimension + nhóm thuế lúc TẠO; PATCH chỉ đổi được tài khoản /
 * số hoá đơn / diễn giải — server trả `pending.create_only` để màn này nói rõ. AX là nguồn
 * chuẩn: mỗi lần mở tab (và sau mỗi thao tác) server đọc lại bút toán từ AX.
 * Không tự toast.error: addRejectedMatcher của slice đã toast mọi PaymentRequest/* bị reject.
 */

// One section per journal; an older server answers with a single journal at the top level.
const sectionsOf = (data) => {
  if (data?.journals?.length) return data.journals;
  if (!data?.lines) return [];
  return [{ ...data, name: data.journal_type, label: `Journal - ${data.journal_type}`, header: data.header }];
};

const VALUE_KEYS = [...FIELDS.map((f) => f.key), ...AMOUNT_KEYS];

const linesPayload = (lines, values) => lines.map((l) => ({
  key: lineId(l),
  ...Object.fromEntries(editableFields(l).map((f) => [f.key, values[lineId(l)]?.[f.key] ?? ""])),
  // Vendor line booked against another offset than the Bank payment line (see BankOffsetEditor).
  ...(l.offset_by_bank_line && values[lineId(l)]?.offset_detached
    ? { offset_account_type: values[lineId(l)].offset_account_type || "Ledger", offset_account: values[lineId(l)].offset_account || "" }
    : {}),
  ...(l.manual ? Object.fromEntries(AMOUNT_KEYS.map((k) => [k, values[lineId(l)]?.[k] ?? ""])) : {}),
}));

const JournalWorkbench = ({ paymentRequest: pr }) => {
  const dispatch = useDispatch();
  const workspaceRef = useRef(null);
  const toolbarRef = useRef(null);
  const [wb, setWb] = useState(null);
  const [activeTab, setActiveTab] = useState(null);
  // Per journal (API / APP): its header, the lines on screen (the server's, plus manual
  // lines added / minus removed since the last save) and the values typed into them.
  const [headerBy, setHeaderBy] = useState({});
  const [linesBy, setLinesBy] = useState({});
  const [valuesBy, setValuesBy] = useState({});
  // Built lines taken out of each journal (sent as removed: true on save).
  const [removedBy, setRemovedBy] = useState({});
  const [payment, setPayment] = useState({});
  // AX vendor just picked on the Vendor line (before Save); null = use the saved one.
  const [vendorPreview, setVendorPreview] = useState(null);
  const [settingsTab, setSettingsTab] = useState(null); // "Settings → HSBC" link from the payment details
  const isAdmin = useSelector((s) => s.profile_user?.data?.is_admin);
  const [busy, setBusy] = useState(null);
  const [adjusting, setAdjusting] = useState(false);
  // Bumped after every ERP action so the ERP log panel re-reads.
  const [logRefresh, setLogRefresh] = useState(0);

  const apply = useCallback((data) => {
    const sections = sectionsOf(data);
    setWb(data);
    setHeaderBy(Object.fromEntries(sections.map((s) => [s.name, headerState(s.header || data.header)])));
    setLinesBy(Object.fromEntries(sections.map((s) => [s.name, s.lines || []])));
    setValuesBy(Object.fromEntries(sections.map((s) => [s.name, toValues(s.lines)])));
    setRemovedBy(Object.fromEntries(sections.map((s) => [s.name, s.removed_lines || []])));
    setPayment(data?.payment || {});
    setVendorPreview(null);
    setActiveTab((current) => (sections.some((s) => s.name === current) ? current : sections[0]?.name || null));
  }, []);

  const reload = useCallback(
    () => dispatch(getJournalWorkbench(pr.id)).unwrap().then((r) => apply(r.data)),
    [dispatch, pr.id, apply]
  );

  useEffect(() => {
    reload().catch(() => setWb({ error: true }));
  }, [reload, pr.status, pr.bank?.status]);

  const sections = useMemo(() => sectionsOf(wb), [wb]);
  const vendorFromPo = !!wb?.vendor_from_po;

  const dirty = useMemo(() => {
    if (!sections.length) return false;
    if (JSON.stringify(payment) !== JSON.stringify(wb.payment || {})) return true;
    return sections.some((section) => {
      if (headerBy[section.name] && headerChanged(headerBy[section.name], section.header || wb.header, { vendorFromPo })) return true;
      const lines = linesBy[section.name] || [];
      const values = valuesBy[section.name] || {};
      if (lines.map(lineId).join("|") !== (section.lines || []).map(lineId).join("|")) return true;
      const base = toValues(section.lines);
      return lines.some((l) => VALUE_KEYS.some((k) => (values[lineId(l)]?.[k] ?? "") !== (base[lineId(l)]?.[k] ?? "")));
    });
  }, [wb, sections, headerBy, linesBy, valuesBy, payment, vendorFromPo]);

  // Transaction text VN follows EN while the two are the same (or VN is empty);
  // once the accountant types their own VN it stays.
  const setValue = (name) => (key, field, value) =>
    setValuesBy((all) => {
      const row = (all[name] || {})[key] || {};
      const vnFollows = field === "txt" && (!String(row.peb_txt || "").trim() || row.peb_txt === row.txt);
      return { ...all, [name]: { ...all[name], [key]: { ...row, [field]: value, ...(vnFollows ? { peb_txt: value } : {}) } } };
    });

  const addLine = (name) => () => {
    const key = newManualKey();
    const currency = headerBy[name]?.currency || sections[0]?.lines?.[0]?.currency;
    setLinesBy((all) => ({ ...all, [name]: renumber([...(all[name] || []), { key, manual: true, label: "Manual line", account_type: "Ledger", currency }]) }));
    setValuesBy((all) => ({ ...all, [name]: { ...all[name], [key]: { ...Object.fromEntries(FIELDS.map((f) => [f.key, ""])),
      account_type: "Ledger", offset_account_type: "Ledger", txt: headerBy[name]?.description || wb.header?.description || "", debit: "", credit: "" } } }));
  };

  // The amount the bank pays: the APP vendor line's Debit / Credit, in the header currency.
  const paymentAmountOf = (name) => {
    const line = (linesBy[name] || []).find((l) => l.counterparty_line);
    return {
      amount: line ? (Number(line.debit) || 0) + (Number(line.credit) || 0) : null,
      currency: headerBy[name]?.currency || line?.currency || "VND",
    };
  };

  // A manual line is simply dropped; a built line is set aside, to be restored or
  // sent as removed on save.
  const removeLine = (name) => (key) => {
    const line = (linesBy[name] || []).find((l) => lineId(l) === key);
    setLinesBy((all) => ({ ...all, [name]: renumber((all[name] || []).filter((l) => lineId(l) !== key)) }));
    if (line && !line.manual) {
      setRemovedBy((all) => ({ ...all, [name]: [...(all[name] || []), line] }));
      return;
    }
    setValuesBy((all) => {
      const rest = { ...(all[name] || {}) };
      delete rest[key];
      return { ...all, [name]: rest };
    });
  };

  const restoreLine = (name) => (key) => {
    const line = (removedBy[name] || []).find((l) => lineId(l) === key);
    if (!line) return;
    setRemovedBy((all) => ({ ...all, [name]: (all[name] || []).filter((l) => lineId(l) !== key) }));
    setLinesBy((all) => ({ ...all, [name]: renumber([...(all[name] || []), line]) }));
    setValuesBy((all) => ({ ...all, [name]: { ...toValues([line]), ...all[name] } }));
  };

  const journalLinesPayload = (name) => [
    ...linesPayload(linesBy[name] || [], valuesBy[name] || {}),
    ...(removedBy[name] || []).map((l) => ({ key: lineId(l), removed: true })),
  ];

  // refreshPage: tạo / tạo lại đổi số bút toán, tức đổi cổng chặn ký và các card ở tab Request.
  const run = async (key, thunk, { refreshPage = false } = {}) => {
    setBusy(key);
    try {
      const res = await dispatch(thunk).unwrap();
      if (res?.data) apply(res.data);
      if (res?.warning) toast.warning([res.message, ...(res.warnings || [])].join("\n"), { autoClose: 12000, style: { whiteSpace: "pre-line" } });
      else if (res?.message) toast.success(res.message);
      if (refreshPage) dispatch(getPaymentRequest(pr.id));
      return true;
    } catch {
      // A create that stopped half way (API in ERP, APP refused) changed the request:
      // re-read it, so the journal that did land shows as created.
      if (refreshPage) {
        reload().catch(() => {});
        dispatch(getPaymentRequest(pr.id));
      }
      return false;
    } finally {
      setBusy(null);
      setLogRefresh((n) => n + 1);
    }
  };

  // Saves everything on screen; `options` also switches the journals of the request
  // (Journal name, Payment journal on / off), so the server rebuilds the tabs.
  const save = (options, key = "save") => {
    const [primary, ...others] = sections;
    const editable = (section) => !!section.permissions?.allow_refill;
    const header = (name) => headerPayload(headerBy[name] || headerState(), { vendorFromPo });
    return run(key, saveJournalOverrides({
      id: pr.id,
      header: primary ? header(primary.name) : undefined,
      payment: wb.payment_editable ? payment : undefined,
      // A locked journal is not sent: the server keeps what it has.
      lines: primary && editable(primary) ? journalLinesPayload(primary.name) : undefined,
      journal_lines: others.length
        ? Object.fromEntries(others.filter(editable).map((s) => [s.name, journalLinesPayload(s.name)]))
        : undefined,
      journal_headers: others.length ? Object.fromEntries(others.filter(editable).map((s) => [s.name, header(s.name)])) : undefined,
      options,
    }), { refreshPage: !!wb.adjustment?.open });
  };

  const setPaymentJournal = async (on) => {
    if (!on) {
      const confirmed = await confirm({
        title: "Remove the Payment journal - APP?",
        message: "Its lines, header and payment details typed on this tab are discarded. It is not in ERP yet, so nothing " +
          "changes in AX. Without APP the request cannot be paid through the bank.",
        type: "danger",
        confirmText: "Remove APP",
      });
      if (!confirmed) return;
    }
    const ok = await save({ primary_journal: wb.options?.primary_journal || "API", with_payment_journal: on }, on ? "add-app" : "remove-app");
    if (ok && on) setActiveTab("APP");
  };

  useLayoutEffect(() => {
    const toolbar = toolbarRef.current;
    if (!toolbar) return undefined;
    const update = () => workspaceRef.current?.style.setProperty('--journal-toolbar-height', `${Math.ceil(toolbar.getBoundingClientRect().height)}px`);
    update();
    if (typeof ResizeObserver === 'undefined') return undefined;
    const observer = new ResizeObserver(update);
    observer.observe(toolbar);
    return () => observer.disconnect();
  }, [wb]);

  if (!wb) {
    return (
      <div className="tw-bg-geist-bg tw-rounded-geist-md tw-border tw-border-solid tw-border-geist-border tw-p-6 tw-text-[13px] tw-text-geist-fg-3">
        <FaSpinner className="tw-animate-spin tw-mr-2" size={11} /> Loading journal…
      </div>
    );
  }
  if (wb.error) {
    return <div className="tw-p-6 tw-text-[13px] tw-text-geist-fg-3">The journal could not be loaded.</div>;
  }

  const perms = wb.permissions || {};
  const options = wb.options || {};
  const active = sections.find((s) => s.name === activeTab) || sections[0];
  const anyCreated = sections.some((s) => s.journal);
  const missing = sections.filter((s) => !s.journal);
  // The vendor of a journal is picked on its Vendor line (Account column): it sets the
  // journal header's vendor, which Save checks against AX. Not for PO-based journals
  // (the PO gives the vendor) nor employee reimbursements (account set in the header).
  const loadVendorFromAx = async (code) => {
    try {
      const details = await fetchVendorDetails(pr.id, code);
      // Everything about who gets paid follows the new vendor — at once, nothing left over.
      const fromAx = details?.payment || {};
      setPayment((p) => ({ ...p, ...Object.fromEntries(VENDOR_PAYMENT_FIELDS.map((k) => [k, fromAx[k] ?? ""])) }));
      setVendorPreview(details ? { vendor: details.erp_vendor, sources: details.sources, payment: fromAx } : null);
      if (details?.erp_vendor?.bank_account) toast.success(`Bank details of ${code} loaded from AX — Save to keep them.`);
    } catch (e) {
      toast.warn(e?.response?.data?.error || `Could not read vendor ${code} from AX.`);
    }
  };
  const vendorCounterparty = (section) => {
    const header = headerBy[section.name];
    if (vendorFromPo || !header || header.counterparty === "employee") return undefined;
    const vendorLine = (linesBy[section.name] || []).find((l) => l.counterparty_line);
    return {
      value: header.vendor, name: header.vendorName, requestId: pr.id,
      editable: !!section.permissions?.allow_refill && !busy,
      placeholder: section.name === "APP" && sections.length === 1 && pr.form_type === "qf02" ? "Vendor prepayment account…" : "Vendor code in AX…",
      onChange: (code, name) => {
        setHeaderBy((all) => ({ ...all, [section.name]: { ...all[section.name], vendor: code, vendorName: name } }));
        // Keep the line on the same code, so no stale line override hides the vendor.
        if (vendorLine) setValue(section.name)(lineId(vendorLine), "account", code);
        // Picked from the list (it carries a name): load who gets paid from AX.
        if (code && name) loadVendorFromAx(code);
      },
    };
  };
  // Why "Create journals in ERP" is greyed out: the server's reason (wrong step / not
  // the Accountant / problems to fix), or unsaved changes on this tab.
  const createHint = !perms.allow_create
    ? (wb.create_blocked_reason || (missing.length > 0 ? "Fix the points listed above before creating the journals." : ""))
    : dirty ? "Save your changes first." : "";
  // HSBC* offset (HSBC01VND / HSBC01USD) = online payment, released to HSBC right after the BOD signs.
  const hsbcAccounts = [...new Set(sections.flatMap((s) => hsbcOffsetAccounts(linesBy[s.name] || [], valuesBy[s.name] || {})))];
  // HSBC payment setup (notice, payment details, HSBC file) as soon as the request has
  // a Payment journal (APP) — in ERP or not yet — paying out of an HSBC* account: the
  // bank pays against APP, and its payment details are filled in before the next step.
  const hasAppJournal = sections.some((s) => s.name === "APP");
  const appHsbcAccounts = hsbcOffsetAccounts(linesBy.APP || [], valuesBy.APP || {});
  const showHsbcPayment = hasAppJournal && appHsbcAccounts.length > 0;
  // The HSBC file panel (checklist, XML, GPG) is for Accounting and admins — like the API it reads.
  const showHsbcFilePanel = showHsbcPayment && !!pr.permissions?.allow_finance_insight;
  const canAddPaymentJournal = options.payment_journal_optional && !options.with_payment_journal && perms.allow_refill;
  // Problems every journal shares (e.g. the vendor account) are said once, above the tabs.
  const common = sections.length > 1
    ? (sections[0].problems || []).filter((p) => sections.every((s) => (s.problems || []).includes(p)))
    : [];
  // Update in ERP (toolbar): every journal already in AX whose saved values differ —
  // API and APP in one go, whichever tab is open. Stops at the first refusal.
  const pushable = sections.filter((s) => s.permissions?.allow_push && s.state === "on_erp");
  const pushPending = pushable.filter((s) => (s.pending?.patch || []).length > 0);
  const pushAll = async () => {
    for (const s of pushPending) {
      if (!(await run(`push:${s.name}`, pushJournalChanges({ id: pr.id, journal: s.name })))) break;
    }
  };
  const accountTypes = wb.account_types?.length ? wb.account_types : DEFAULT_ACCOUNT_TYPES;
  const taxGroups = wb.item_sales_tax_groups?.length ? wb.item_sales_tax_groups : DEFAULT_ITEM_SALES_TAX_GROUPS;
  const card = "tw-bg-geist-bg tw-rounded-geist-md tw-border tw-border-solid tw-border-geist-border tw-shadow-geist-card";

  return (
    <div ref={workspaceRef} className="payment-journal-workbench tw-flex tw-flex-col tw-gap-4">
      <section className={card}>
        <header ref={toolbarRef} className="payment-journal-toolbar">
          <div className="payment-journal-toolbar-row">
            <div className="payment-journal-toolbar-info">
              <h3 className="tw-m-0 tw-text-sm tw-font-semibold tw-text-geist-fg">{active?.label || "Journal"}</h3>
            </div>
            <div className="payment-journal-actions" role="toolbar" aria-label="Journal actions">
              {anyCreated && (
                <ActionButton icon="bi-arrow-repeat" loading={busy === "reload"} disabled={!!busy} onClick={() => run("reload", getJournalWorkbench(pr.id))}>
                  {busy === "reload" ? "Reading…" : "Reload from AX"}
                </ActionButton>
              )}
              {perms.allow_adjust && (
                <ActionButton tone="secondary" icon="bi-unlock" disabled={!!busy} onClick={() => setAdjusting(true)}
                  title="Approved: change the Payment journal (e.g. the bank) and send it to Head of Finance and BOD again">
                  Adjust APP & re-approve
                </ActionButton>
              )}
              {perms.allow_cancel_adjust && (
                <ActionButton icon="bi-x-lg" loading={busy === "cancel-adjust"} disabled={!!busy}
                  onClick={() => run("cancel-adjust", cancelJournalAdjustment(pr.id), { refreshPage: true })}>
                  Cancel adjustment
                </ActionButton>
              )}
              {perms.allow_refill && (
                <ActionButton tone="secondary" icon="bi-check2" loading={busy === "save"} disabled={!dirty || !!busy} onClick={() => save()}>
                  {busy === "save" ? "Saving…" : "Save"}
                </ActionButton>
              )}
              {pushable.length > 0 && (
                <ActionButton tone="primary" icon="bi-cloud-arrow-up" loading={String(busy || "").startsWith("push:")}
                  disabled={dirty || !!busy || pushPending.length === 0}
                  title={dirty ? "Save your changes first." : pushPending.length === 0 ? "Nothing changed since the journals were created." : undefined}
                  onClick={pushAll}>
                  {String(busy || "").startsWith("push:") ? "Updating…" : pushPending.length > 1 ? "Update journals in ERP" : "Update in ERP"}
                </ActionButton>
              )}
              {(perms.allow_create || (missing.length > 0 && perms.allow_refill)) && (
                <ActionButton tone="primary" icon="bi-cloud-upload" loading={busy === "create"} disabled={!perms.allow_create || dirty || !!busy}
                  title={createHint || (hsbcAccounts.length ? "HSBC online payment: paid through HSBC right after the BOD approves" : undefined)}
                  onClick={() => run("create", createJournalFromWorkbench(pr.id), { refreshPage: true })}>
                  {busy === "create" ? "Creating…" : missing.length > 1 ? "Create journals in ERP" : "Create journal in ERP"}
                </ActionButton>
              )}
            </div>
          </div>
        </header>

        <div className="tw-p-5 tw-flex tw-flex-col tw-gap-3">
          {wb.adjustment?.open && (
            <p role="status" className="tw-m-0 tw-rounded-geist-sm tw-bg-[#fff8ee] tw-px-3 tw-py-2 tw-text-[13px] tw-text-[#a05e03]">
              <i className="bi bi-unlock tw-mr-1" aria-hidden="true" />
              Adjusting after approval ({wb.adjustment.opened_by}): {wb.adjustment.reason}. Saving sends the request to
              Head of Finance and BOD again; update the journal in ERP if it is already there.
            </p>
          )}
          {wb.handled_by?.name && (
            <p role="note" aria-label="Handled by" className="tw-m-0 tw-text-[13px] tw-text-geist-fg-2">
              <i className="bi bi-person-workspace tw-mr-1" aria-hidden="true" />
              Being handled by <strong>{wb.handled_by.name}</strong>
              {wb.handled_by.at ? ` · since ${String(wb.handled_by.at).replace("T", " ").slice(0, 16)}` : ""}
            </p>
          )}
          {createHint && missing.length > 0 && (
            <p role="note" aria-label="Why journals cannot be created" className="tw-m-0 tw-text-[13px] tw-text-geist-fg-3">
              <i className="bi bi-info-circle tw-mr-1" aria-hidden="true" />{createHint}
            </p>
          )}
          {showHsbcPayment && <HsbcOnlinePaymentNotice accounts={appHsbcAccounts} />}
          {(wb.reference_only_fields || []).length > 0 && <p role="note" className="tw-m-0 tw-text-[13px] tw-text-geist-fg-2">
            Extended invoice-field writes are disabled for this ERP connection. Terms of payment, due date and document details remain saved on this request.
          </p>}

          {common.length > 0 && <p className="tw-m-0 tw-text-[13px] tw-font-semibold tw-text-geist-fg">Complete these fields before creating the Journal:</p>}
          {common.map((p) => (
            <div key={p} className="tw-rounded-geist-sm tw-border tw-border-solid tw-border-[#f7e2c0] tw-bg-[#fff8ee] tw-px-4 tw-py-2.5 tw-text-[13px] tw-text-[#a05e03]">{p}</div>
          ))}

          <div className="payment-journal-tabs" role="tablist" aria-label="Journals">
            {sections.map((s) => (
              <div key={s.name} className={`payment-journal-tab ${s.name === active?.name ? "payment-journal-tab-active" : ""}`}>
                <button type="button" role="tab" aria-selected={s.name === active?.name} onClick={() => setActiveTab(s.name)}>
                  <span>{s.label}</span>
                  <StateBadge state={s.state} />
                </button>
                {s.name === "APP" && options.payment_journal_optional && options.can_remove_payment_journal && perms.allow_refill && (
                  <button type="button" className="payment-journal-tab-remove" aria-label="Remove Payment journal - APP"
                    title="Remove the Payment journal — only while ERP has not been called for it" disabled={!!busy} onClick={() => setPaymentJournal(false)}>
                    <FaTimes aria-hidden="true" size={10} />
                  </button>
                )}
              </div>
            ))}
            {canAddPaymentJournal && (
              <button type="button" className="payment-journal-tab-add" disabled={!!busy} onClick={() => setPaymentJournal(true)}
                title="The bank instruction needs a Payment journal (APP)">
                {busy === "add-app" ? <FaSpinner aria-hidden="true" size={10} className="tw-animate-spin" /> : <FaPlus aria-hidden="true" size={10} />} {busy === "add-app" ? "Adding…" : "Add Payment journal (APP)"}
              </button>
            )}
          </div>

          {active && (
            <div role="tabpanel" aria-label={active.label}>
              <JournalSection key={active.name}
                section={{ ...active, problems: (active.problems || []).filter((p) => !common.includes(p)) }}
                lines={linesBy[active.name] || []} values={valuesBy[active.name] || {}} requestId={pr.id}
                accountTypes={accountTypes} taxGroups={taxGroups} createOnly={wb.create_only || []}
                busy={busy}
                header={headerBy[active.name] && (
                  <JournalHeaderFields state={headerBy[active.name]} section={active} primary={active.name === sections[0]?.name}
                    options={options} editable={!!active.permissions?.allow_refill && !busy} created={!!active.journal}
                    requestId={pr.id} paymentRequest={pr} vendorFromPo={vendorFromPo}
                    onChange={(next) => setHeaderBy((all) => ({ ...all, [active.name]: next }))}
                    onJournalNameChange={(name) => save({ primary_journal: name, with_payment_journal: options.with_payment_journal && name === "API" }, "journal-name")} />
                )}
                counterparty={vendorCounterparty(active)}
                footer={active.name === "APP" && wb.payment && showHsbcPayment && (
                  // HSBC online payment (APP in ERP, HSBC* offset): who receives the money —
                  // bank name and BIC included — goes into the bank file.
                  <PaymentDetailsFields payment={payment} onChange={setPayment} requestId={pr.id} hsbc
                    disabled={!perms.allow_refill || !wb.payment_editable || !!busy}
                    lockRecipient={!!wb.single_vendor_request && (headerBy[sections[0]?.name]?.counterparty || "vendor") === "vendor"}
                    paymentAmount={paymentAmountOf("APP")} xmlV3={wb.hsbc_xml_v3} erpVendor={vendorPreview?.vendor || wb.erp_vendor}
                    sources={vendorPreview?.sources || wb.payment_sources} sourcePayment={vendorPreview?.payment || wb.payment}
                    onOpenSettings={isAdmin ? setSettingsTab : undefined} />
                )}
                onChange={setValue(active.name)} onAddLine={addLine(active.name)} onRemoveLine={removeLine(active.name)}
                removedLines={removedBy[active.name] || []} onRestoreLine={restoreLine(active.name)}
                />
            </div>
          )}

          {showHsbcFilePanel && <HsbcPaymentFilePanel paymentRequest={pr} refreshKey={logRefresh} />}
          {isAdmin && (
            <PaymentRequestSettingsModal show={!!settingsTab} onHide={() => setSettingsTab(null)} isAdmin initialTab={settingsTab || "hsbc_keys"} />
          )}

          {pr.permissions?.allow_finance_insight && (
            <ErpLogPanel requestId={pr.id} journalNames={wb.journal_names || [wb.journal_type].filter(Boolean)} refreshKey={logRefresh} />
          )}
        </div>
      </section>

      <JournalAdjustmentModal show={adjusting} code={pr.code} onHide={() => setAdjusting(false)}
        onSubmit={(reason) => run("adjust", startJournalAdjustment({ id: pr.id, reason }), { refreshPage: true })} />
    </div>
  );
};

JournalWorkbench.propTypes = {
  paymentRequest: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
    code: PropTypes.string,
    status: PropTypes.string,
    form_type: PropTypes.string,
    bank: PropTypes.shape({
      status: PropTypes.string,
    }),
    permissions: PropTypes.shape({
      allow_finance_insight: PropTypes.bool,
    }),
  }).isRequired,
};

export default JournalWorkbench;
